import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { VStack } from 'tio-ui/components/layout';
import Text from 'tio-ui/components/text';
import { formatCurrency } from 'tio-ui/format';
import Chip from 'tio-ui/components/chip';
import GlassCard from 'tio-ui/components/glass-card';
import {
  PhSun,
  PhMoon,
  PhCloudSun,
  PhTrendDown,
  PhPiggyBank,
  PhGraduationCap,
  PhCaretRight,
  PhHandshake,
  PhCompass,
  PhBank,
  PhReceipt,
  PhChartLineUp,
  PhBuildings,
  PhHeart,
  PhShieldCheck,
  PhFileText,
  PhClipboardText,
  PhStamp,
} from 'ember-phosphor-icons';
import type ReimaginedDataService from 'tio-employee/services/reimagined-data';
import type SessionContextService from 'tio-employee/services/session-context';
import type { ReimaginedHomeModel } from 'tio-employee/routes/authenticated/reimagined/home';

import SmartActionCard from 'tio-employee/components/reimagined/smart-action-card';
import type { SmartAction } from 'tio-employee/components/reimagined/smart-action-card';
import BenefitCard from 'tio-employee/components/reimagined/benefit-card';
import type { BenefitOpportunity } from 'tio-employee/components/reimagined/benefit-card';
import DataIndicator from 'tio-employee/components/reimagined/data-indicator';
import type { DataSource } from 'tio-employee/components/reimagined/data-indicator';
import PageHero from 'tio-ui/components/page-hero';

interface ReimaginedHomeSignature {
  Args: {
    model: ReimaginedHomeModel;
  };
}

// Unified financial summary — user doesn't care which "product" this comes from
interface UnifiedFinancials {
  totalLoans: number;
  monthlyPayment: number;
  employerHelpYTD: number;
  employerHelpLifetime: number;
  projectedForgiveness: number;
  taxSavingsYTD: number;
  yearsToPayoff: number;
}

export default class ReimaginedHome extends Component<ReimaginedHomeSignature> {
  @service declare reimaginedData: ReimaginedDataService;
  @service declare sessionContext: SessionContextService;
  @tracked showAllActions = false;

  // ── Data source helpers ─────────────────────────────────────────────

  get dataSourceInfo() {
    return this.args.model.dataSourceInfo;
  }

  get studentLoansSource(): DataSource {
    return this.dataSourceInfo.studentLoans;
  }

  get section127Source(): DataSource {
    return this.dataSourceInfo.section127Used;
  }

  // ── User & employer context ─────────────────────────────────────────

  get employerName() {
    return (
      this.sessionContext.company?.name ||
      this.sessionContext.currentEmployee?.company?.name ||
      'Your employer'
    );
  }

  get firstName() {
    return (
      this.sessionContext.user?.person?.firstName ||
      this.sessionContext.currentEmployee?.firstName ||
      ''
    );
  }

  get greeting() {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  }

  get greetingIcon() {
    const hour = new Date().getHours();
    if (hour < 12) return PhSun;
    if (hour < 17) return PhCloudSun;
    return PhMoon;
  }

  // ── Feature eligibility ─────────────────────────────────────────────

  get financialSnapshot() {
    return this.args.model.financialSnapshot;
  }

  get pslfData() {
    return this.args.model.pslfData;
  }

  get tasData() {
    return this.args.model.tasData;
  }

  get section127Info() {
    return this.args.model.section127Info;
  }

  get showStudentLoans() {
    return this.reimaginedData.showStudentLoanRepayment;
  }

  get showTuitionAssistance() {
    return this.reimaginedData.showTuitionAssistance;
  }

  get hasPslfForms() {
    return this.reimaginedData.showPslf && this.pslfData.forms.length > 0;
  }

  get pslfQualifyingPayments() {
    return Math.max(
      0,
      ...this.pslfData.accounts.flatMap((a) => a.loans.map((l) => l.qualifyingPayments ?? 0))
    );
  }

  get pslfYearsRemaining() {
    return Math.ceil((120 - this.pslfQualifyingPayments) / 12);
  }

  get pslfEstimatedForgiveness() {
    return this.pslfData.estimatorResults?.estimatedForgivenessAmount ?? 0;
  }

  get tasEnrolled() {
    return (
      this.tasData.programs.length > 0 ||
      this.tasData.applications.length > 0 ||
      this.tasData.summary.activeCount > 0
    );
  }

  get tasPendingApplications() {
    return this.tasData.summary.pendingCount;
  }

  // ── Admin role checks ───────────────────────────────────────────────

  get isPslfAdmin() {
    return !!this.sessionContext.rolesForCurrentCompany.find(
      (e) => e.relationshipType === 'PSLF_ADMIN'
    );
  }

  get isTasAdmin() {
    return !!this.sessionContext.rolesForCurrentCompany.some(
      (role) =>
        role.relationshipType.startsWith('TAS.Approver') ||
        role.relationshipType === 'TAS.Admin.SUPERUSER' ||
        role.relationshipType === 'TAS.Admin.PROGRAM_ADMIN'
    );
  }

  // ── Unified financials ──────────────────────────────────────────────

  get financials(): UnifiedFinancials {
    const loans = this.financialSnapshot.studentLoans;
    const contributions = this.financialSnapshot.employerContributions;

    return {
      totalLoans: loans.totalBalance,
      monthlyPayment: loans.monthlyPayment,
      employerHelpYTD: contributions.yearToDate,
      employerHelpLifetime: contributions.lifetime,
      projectedForgiveness: this.hasPslfForms ? this.pslfEstimatedForgiveness : 0,
      taxSavingsYTD: this.financialSnapshot.optimizationPotential.taxSavingsYtd,
      yearsToPayoff: this.calculateYearsToPayoff(loans.estimatedPayoffDate),
    };
  }

  calculateYearsToPayoff(dateStr: string) {
    const payoffDate = new Date(dateStr);
    const now = new Date();
    const diffYears = (payoffDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24 * 365);
    return Math.max(0, Math.round(diffYears * 10) / 10);
  }

  // ── Smart actions (only data-backed actions) ────────────────────────

  get smartActions(): SmartAction[] {
    const actions: SmartAction[] = [];
    const section127 = this.financialSnapshot.section127;

    // ── Admin actions (show first for admins) ───────────────────────

    if (this.isPslfAdmin) {
      actions.push({
        id: 'pslf-admin-review',
        priority: 'urgent',
        category: 'deadline',
        icon: PhStamp,
        title: 'PSLF forms awaiting your review',
        description: 'Employee certification forms need employer approval',
        cta: 'Review forms',
        route: 'authenticated.admin.pslf.forms',
      });
    }

    if (this.isTasAdmin) {
      actions.push({
        id: 'tas-admin-approvals',
        priority: 'urgent',
        category: 'deadline',
        icon: PhClipboardText,
        title: 'Tuition applications need approval',
        description: 'Review and approve pending tuition assistance requests',
        cta: 'Open approval queue',
        route: 'authenticated.admin.tas.approval-queue',
      });
    }

    // ── Employee actions ────────────────────────────────────────────

    // Unused benefits reminder
    if (section127.remaining > 2000) {
      actions.push({
        id: 'use-benefits',
        priority: 'urgent',
        category: 'money',
        icon: PhPiggyBank,
        title: `You have ${formatCurrency(section127.remaining)} in unused benefits`,
        description: "Tax-free employer money expires Dec 31 if you don't use it",
        impact: `Save ${formatCurrency(section127.remaining * 0.25)} in taxes`,
        cta: 'Use my benefits',
        route: 'authenticated.reimagined.finance',
      });
    }

    // TAS pending applications
    if (this.tasPendingApplications > 0) {
      const count = this.tasPendingApplications;
      actions.push({
        id: 'tas-pending',
        priority: 'high',
        category: 'money',
        icon: PhGraduationCap,
        title: `${count} tuition request${count > 1 ? 's' : ''} pending`,
        description: 'Check the status of your tuition assistance applications',
        cta: 'View status',
        route: 'authenticated.reimagined.benefits',
      });
    }

    // PSLF certification
    if (this.hasPslfForms) {
      const payments = this.pslfQualifyingPayments;
      const remaining = 120 - payments;
      actions.push({
        id: 'pslf-cert',
        priority: remaining <= 12 ? 'urgent' : 'high',
        category: 'deadline',
        icon: PhFileText,
        title: remaining <= 12 ? 'Almost there! Keep certifying' : 'Annual PSLF certification due',
        description: 'Certify your qualifying payments to stay on track for forgiveness',
        impact: `${payments} of 120 payments certified`,
        cta: 'Certify now',
        route: 'authenticated.pslf',
      });
    }

    // Optimization opportunity (requires both TA and SLR)
    if (this.showStudentLoans && this.showTuitionAssistance) {
      const recommendation = this.financialSnapshot.optimizationPotential;
      if (recommendation.potentialSavings > 1000) {
        actions.push({
          id: 'optimize',
          priority: 'high',
          category: 'money',
          icon: PhChartLineUp,
          title: `Save ${formatCurrency(recommendation.potentialSavings)} more per year`,
          description: 'A better way to split your benefits between loans and tuition',
          impact: `Pay off ${recommendation.projectedPayoffAcceleration} faster`,
          cta: 'See optimization',
          route: 'authenticated.reimagined.finance.optimizer',
        });
      }
    }

    const priorityOrder = { urgent: 0, high: 1, medium: 2, low: 3 };
    return actions.sort((a, b) => priorityOrder[a.priority] - priorityOrder[b.priority]);
  }

  get visibleActions() {
    return this.showAllActions ? this.smartActions : this.smartActions.slice(0, 4);
  }

  toggleShowAll = () => {
    this.showAllActions = !this.showAllActions;
  };

  // ── Benefit opportunities ───────────────────────────────────────────

  get benefitOpportunities(): BenefitOpportunity[] {
    const opportunities: BenefitOpportunity[] = [];
    const section127 = this.financialSnapshot.section127;

    if (this.showStudentLoans) {
      opportunities.push({
        id: 'slr',
        title: 'Student Loan Payments',
        description: `${this.employerName} pays toward your student loans`,
        value: `$${section127.loanAllocation.toLocaleString()}/year`,
        enrolled: true,
        eligible: true,
        icon: PhBank,
      });
    }

    if (this.showTuitionAssistance) {
      opportunities.push({
        id: 'tas',
        title: 'Tuition Assistance',
        description: 'Get help paying for courses and certifications',
        value: `$${section127.tuitionAllocation.toLocaleString()}/year`,
        enrolled: this.tasEnrolled,
        eligible: true,
        icon: PhGraduationCap,
      });
    }

    if (this.hasPslfForms) {
      opportunities.push({
        id: 'pslf',
        title: 'Loan Forgiveness (PSLF)',
        description: 'Work at a qualifying employer = loans forgiven after 10 years',
        value: 'Up to 100% forgiven',
        enrolled: true,
        eligible: true,
        icon: PhShieldCheck,
      });
    }

    if (this.showTuitionAssistance || this.showStudentLoans) {
      opportunities.push({
        id: 'section127',
        title: 'Tax-Free Education Benefit',
        description: 'Up to $5,250/year in tax-free education assistance',
        value: `$${section127.remaining.toLocaleString()} remaining`,
        deadline: 'Dec 31',
        enrolled: true,
        eligible: true,
        icon: PhReceipt,
      });
    }

    return opportunities;
  }

  // ── Template ────────────────────────────────────────────────────────

  <template>
    {{! template-lint-disable no-bare-strings }}
    <VStack class="w-full min-w-0 gap-6 pb-8">

      {{! ─── HERO: Greeting + Financial KPIs ─────────────────────────── }}
      <DataIndicator
        @source={{this.studentLoansSource}}
        @showBadge={{true}}
        @dataSourceId="student-loans"
      >
        <PageHero @gradient="cool" @decoration="dots" @rounded="2xl">
          <:default>
            <div class="tio-home-hero__greeting">
              {{#let this.greetingIcon as |GreetingIcon|}}
                <GreetingIcon @size={{18}} />
              {{/let}}
              <span class="text-sm">{{this.greeting}}, {{this.firstName}}</span>
            </div>

            <h1 class="tio-fg-inverse tio-text-2xl-bold md:text-3xl mb-6">
              Your Benefits Dashboard
            </h1>

            <div class="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
              {{#if this.showStudentLoans}}
                <GlassCard
                  @icon={{PhBank}}
                  @label="Student Loans"
                  @value={{formatCurrency this.financials.totalLoans}}
                >
                  <:subtext>
                    <PhTrendDown @size={{14}} class="tio-home-hero__trend" />
                    <span>{{this.financials.yearsToPayoff}} years to payoff</span>
                  </:subtext>
                </GlassCard>

                <GlassCard
                  @icon={{PhHandshake}}
                  @label="Employer Help YTD"
                  @value={{formatCurrency this.financials.employerHelpYTD}}
                  @accent="default"
                  @subtext="{{formatCurrency this.financials.employerHelpLifetime}} lifetime"
                />
              {{else}}
                <GlassCard
                  @icon={{PhGraduationCap}}
                  @label="Tuition Available"
                  @value={{formatCurrency this.section127Info.remaining}}
                  @subtext="Annual tax-free benefit"
                  @accent="default"
                />

                <GlassCard
                  @icon={{PhHandshake}}
                  @label="Employer Help YTD"
                  @value={{formatCurrency this.financials.employerHelpYTD}}
                  @accent="default"
                  @subtext="{{formatCurrency this.financials.employerHelpLifetime}} lifetime"
                />
              {{/if}}

              <GlassCard
                @icon={{PhReceipt}}
                @label="Tax Savings YTD"
                @value={{formatCurrency this.financials.taxSavingsYTD}}
                @accent="default"
                @subtext="From Section 127 benefit"
              />

              {{#if this.hasPslfForms}}
                <GlassCard
                  @icon={{PhShieldCheck}}
                  @label="Projected Forgiveness"
                  @value={{formatCurrency this.financials.projectedForgiveness}}
                  @accent="brand"
                  @subtext="{{this.pslfQualifyingPayments}}/120 payments · {{this.pslfYearsRemaining}} years left"
                />
              {{else}}
                <GlassCard
                  @icon={{PhReceipt}}
                  @label="Section 127 Remaining"
                  @value={{formatCurrency this.financialSnapshot.section127.remaining}}
                  @accent="default"
                  @subtext="Resets Dec 31"
                />
              {{/if}}
            </div>
          </:default>
        </PageHero>
      </DataIndicator>

      {{! ─── SMART ACTIONS ────────────────────────────────────────────── }}
      {{#if this.smartActions.length}}
        <div>
          <div class="tio-row-between mb-4">
            <div class="tio-row-tight">
              <PhCompass @size={{24}} class="tio-fg-brand" />
              <h2 class="tio-text-xl-semibold tio-fg-primary">What to Do Next</h2>
            </div>
            <Text @variant="muted" @size="sm">
              {{this.smartActions.length}}
              {{if (gte this.smartActions.length 2) "actions" "action"}}
            </Text>
          </div>

          <div class="space-y-3">
            {{#each this.visibleActions as |smartAction|}}
              <SmartActionCard @item={{smartAction}} />
            {{/each}}
          </div>

          {{#if (gte this.smartActions.length 5)}}
            <button
              type="button"
              class="w-full mt-3 py-3 text-center tio-fg-brand hover:tio-fg-brand tio-text-sm-medium tio-row-tight justify-center"
              {{on "click" this.toggleShowAll}}
            >
              {{#if this.showAllActions}}
                Show fewer
              {{else}}
                Show all
                {{this.smartActions.length}}
                actions
                <PhCaretRight @size={{16}} />
              {{/if}}
            </button>
          {{/if}}
        </div>
      {{/if}}

      {{! ─── YOUR BENEFITS ────────────────────────────────────────────── }}
      <DataIndicator
        @source={{this.section127Source}}
        @showBadge={{true}}
        @dataSourceId="section-127"
      >
        <div class="tio-bg-surface rounded-xl p-6 tio-hairline-card">
          <div class="tio-row-between mb-4">
            <div class="tio-row-tight">
              <PhHeart @size={{24}} class="tio-fg-danger" />
              <h2 class="tio-text-lg-semibold tio-fg-primary">
                Your
                {{this.employerName}}
                Benefits
              </h2>
            </div>
            <Chip @intent="success" class="text-xs">
              {{this.benefitOpportunities.length}}
              active
            </Chip>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            {{#each this.benefitOpportunities as |benefit|}}
              <BenefitCard @benefit={{benefit}} />
            {{/each}}
          </div>
        </div>
      </DataIndicator>

      {{! ─── EMPLOYER APPRECIATION ────────────────────────────────────── }}
      {{#if this.financials.employerHelpLifetime}}
        <div class="tio-bg-success-gradient rounded-xl p-6 tio-fg-inverse">
          <div class="tio-row-loose">
            <div class="tio-home-appreciation__icon">
              <PhBuildings @size={{28}} />
            </div>
            <div>
              <h3 class="tio-fg-inverse tio-text-lg-semibold">
                {{this.employerName}}
                has invested
                {{formatCurrency this.financials.employerHelpLifetime}}
                in you
              </h3>
              <p class="tio-home-appreciation__copy">
                {{#if this.showStudentLoans}}
                  {{#if this.showTuitionAssistance}}
                    Through student loan payments, tuition assistance, and tax-free benefits
                  {{else}}
                    Through student loan payments and tax-free benefits
                  {{/if}}
                {{else}}
                  {{#if this.showTuitionAssistance}}
                    Through tuition assistance and tax-free benefits
                  {{else}}
                    Through tax-free benefits
                  {{/if}}
                {{/if}}
              </p>
            </div>
          </div>
        </div>
      {{/if}}
    </VStack>
  </template>
}
